import { Dialog, Select } from "@rio-cloud/rio-uikit";
import SearchAutocomplete from "../../../../../../form/searchAutocomplete";
import useAssets from "../../../../../../../hooks/useAssets";
import { Asset } from "../../../../../../../models/asset";
import { useEffect, useState } from "react";
import ErrorDialog from "../../../../../../dialogs/ErrorDialog";
import { createTrip } from "../../../../../../../api/routing";
import SuccessDialog from "../../../../../../dialogs/SuccessDialog";
import { Driver } from "../../../../../../../models/driver";
import { assignDriverToOccurrenceDispatch } from "../../../../../../../api/occurrence";
import { Occurrence, OccurrenceDispatch } from "../../../../../../../models/occurrence";
import { fetchUserId } from "../../../../../../../api/fetchUser";
import { useSelector } from "react-redux";
import { getUserProfile } from "../../../../../../../configuration/login/loginSlice";

interface CreateTripDialogProps {
    routeId: string;
    show: boolean;
    onClose: () => void;
    onTripCreated: (driverName: string) => void;
    drivers: Driver[];
    occurrence: Occurrence | null;
    occurrenceDispatch: OccurrenceDispatch;
}

const CreateTripDialog = (props: CreateTripDialogProps) => {
    const { getFilteredAssets } = useAssets();
    const userProfile = useSelector(getUserProfile);
    const [loading, setLoading] = useState<boolean>(false);
    const [form, setForm] = useState<{
        assetId: string;
        assetName: string;
        driverId: string;
        driverName: string;
        profileId: string;
    }>({
        assetId: '',
        assetName: '',
        driverId: '',
        driverName: '',
        profileId: '',
    });

    const [errorDialog, setErrorDialog] = useState<string | null>(null);
    const [successDialog, setSuccessDialog] = useState<string | null>(null);

    useEffect(() => {
        if (props.show) {
            setForm({
                assetId: '',
                assetName: '',
                driverId: '',
                driverName: '',
                profileId: '',
            });
        }
    }, [props.show]);

    
    const handleCreateTrip = async () => {
        const userId = await fetchUserId(userProfile);
        if (!userId) {
            setErrorDialog('Erro ao buscar usuário');
            return;
        }

        setLoading(true);
        try {
            const date = new Date();
            const formattedDate = date.toISOString().replace(/\.\d{3}Z$/, '+00:00');
            const { wasCreated, tripId, error } = await createTrip(props.routeId, userId, form.driverId, form.driverName, form.assetId, form.assetName, formattedDate, form.profileId);
            if (!wasCreated) {
                setErrorDialog(error ?? 'Erro ao criar viagem');
            } else {
                await assignDriverToOccurrenceDispatch(props.occurrence?.id ?? '', props.occurrenceDispatch.id ?? '', form.driverName);
                setSuccessDialog('Viagem criada com sucesso');
            }
        } catch (error) {
            setErrorDialog(error instanceof Error ? error.message : 'Erro ao criar viagem');
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Dialog
                show={props.show}
                onClose={props.onClose}
                showCloseButton
                bsSize={Dialog.SIZE_SM}
            >
                <Dialog.Title title="Criar viagem para o socorro mecânico" />
                <Dialog.Body>
                    <div className="w-full">
                        <div className="text-gray-400 mb-8">
                            Informe um veículo e um motorista que fará o socorro mecânico.
                        </div>

                        <div className={`flex justify-between w-full gap-6 flex-wrap`}>
                            <div className={`form-group form-group-lg w-full`}>
                                <label htmlFor="assetId" className="truncate">Buscar veículo</label>
                                <SearchAutocomplete
                                    id="assetId"
                                    placeholder="Buscar veículo pelo nome ou chassi..."
                                    fetchOptions={async (searchTerm) => {
                                        const response = getFilteredAssets(searchTerm, 50, 0);
                                        return response;
                                    }}
                                    optionLabel={{ name: 'Nome', chassis: 'Chassi' }}
                                    onSelectedOption={(asset: Asset) => {
                                        setForm({
                                            ...form,
                                            assetId: asset.id,
                                            assetName: asset.name,
                                        });
                                    }}
                                />
                            </div>
                        </div>

                        <div className={`form-group form-group-lg w-full`}>
                            <label htmlFor="driverId" className="truncate">Motorista</label>
                            <Select
                                id="driverId"
                                useFilter
                                options={props.drivers.map(driver => ({
                                    id: driver.id ?? '',
                                    label: driver.name,
                                    profileId: driver.profileId ?? '',
                                }))}
                                value={[form.driverId]}
                                onChange={(e) => {
                                    setForm({
                                        ...form,
                                        driverId: e?.id ?? '',
                                        driverName: e?.label ?? '',
                                        profileId: e?.profileId ?? '',
                                    });
                                }}
                            />
                        </div>
                    </div>
                </Dialog.Body>

                <Dialog.Footer>
                    <button 
                        className={`btn btn-primary ${loading ? 'btn-loading' : ''}`} 
                        onClick={handleCreateTrip} 
                        disabled={loading || !form.assetId || !form.driverId}
                    >
                        {loading ? 'Criando...' : 'Criar viagem'} 
                    </button>
                </Dialog.Footer>
            </Dialog>
            
            <ErrorDialog
                show={!!errorDialog}
                onClose={() => setErrorDialog(null)}
                title="Ops! Ocorreu um erro"
                body={errorDialog}
            />

            <SuccessDialog
                show={!!successDialog}
                onClose={() => {
                    setSuccessDialog(null);
                    props.onTripCreated(form.driverName);
                    props.onClose();
                }}
                title="Viagem criada com sucesso"
                body="A viagem foi criada com sucesso. Agora o motorista/mecânico pode consultar a rota. Você pode acessar a viagem no sistema de rotas (routing)."
            />
        </>
    );
};

export default CreateTripDialog;